import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn } from '../../components';

import { chromeForRole } from '../LegalPage/LegalPage';
import LegalContent from '../LegalPage/LegalContent';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { FAQ_CONTENT, FAQ_TITLES, FAQ_META } from './content';
import css from '../LegalPage/LegalPage.module.css';

/**
 * FAQ pages (KidHive) -- a plain-language help page per audience, rendered with the same content
 * blocks + role-adaptive chrome the legal pages use. The audience comes from the route param
 * `/faq/:userType` (parent | provider); an unknown value renders NotFoundPage.
 *
 * PUBLIC (no auth on the route): anyone with the URL can read either FAQ (chrome falls back to the
 * waitlist topbar for anonymous visitors). Visibility is controlled at the LINK level -- only an
 * authenticated user sees the FAQ link for THEIR role in the footer (see FooterContainer). We keep
 * BOTH audiences reachable by URL rather than role-locking the page, since the content isn't
 * sensitive and a provider may want to read the parent FAQ (and vice versa).
 */
export const FaqPageComponent = props => {
  const intl = useIntl();
  const { params, scrollingDisabled, isAuthenticated, currentUser } = props;
  const userType = params?.userType;
  const blocks = FAQ_CONTENT[userType];

  // Unknown audience (a typo'd or "random" :userType) -> Not Found.
  if (!blocks) {
    return <NotFoundPage />;
  }

  const title = FAQ_TITLES[userType];
  // Version + published date under the title, same revision line as the legal pages (client ask).
  const meta = FAQ_META[userType];
  const metaLine = meta
    ? intl.formatMessage(
        { id: 'LegalPage.revisionLine' },
        { version: meta.version, effectiveDate: meta.effectiveDate }
      )
    : null;
  const { topbar, footer } = chromeForRole({ isAuthenticated, currentUser });

  return (
    <Page title={`KidHive | ${title}`} description={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn mainColumnClassName={css.layoutMain} topbar={topbar} footer={footer}>
        <article className={css.content}>
          <header className={css.docHeader}>
            <h1 className={css.h1}>{title}</h1>
            {metaLine ? <p className={css.metaLine}>{metaLine}</p> : null}
          </header>
          <div className={css.body}>
            <LegalContent blocks={blocks} />
          </div>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
  isAuthenticated: state.auth.isAuthenticated,
  currentUser: state.user.currentUser,
});

const FaqPage = compose(connect(mapStateToProps))(FaqPageComponent);

export default FaqPage;
